import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Sparkles, RefreshCw, Mail } from "lucide-react";

/**
 * Phase 29 — "Share with my accountant" modal.
 * 1. On open, fetches a DARA-drafted cover note (pre-filled, fully editable).
 * 2. User edits, optionally enters recipient name + email.
 * 3. POST /xero-hygiene/share/send → backend renders PDF + sends via Resend.
 */
export default function ShareScorecardDialog({ open, onClose, scanId }) {
  const [recipient, setRecipient] = useState({ name: "", email: "" });
  const [coverNote, setCoverNote] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  const draftNote = async () => {
    setDrafting(true);
    try {
      const r = await api.post("/xero-hygiene/share/draft", {
        scan_id: scanId || null,
        recipient_name: recipient.name || null,
      });
      setCoverNote(r.data.draft || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't draft cover note");
    }
    setDrafting(false);
  };

  // Auto-draft when the dialog opens (or when recipient name changes meaningfully)
  useEffect(() => {
    if (open && !coverNote) draftNote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    setRecipient({ name: "", email: "" });
    setCoverNote("");
  };

  const send = async (e) => {
    e.preventDefault();
    if (!recipient.email) {
      toast.error("Recipient email is required.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/xero-hygiene/share/send", {
        scan_id: scanId || null,
        recipient_email: recipient.email,
        recipient_name: recipient.name || null,
        cover_note: coverNote,
      });
      if (r.data.delivered) {
        toast.success(`Sent to ${recipient.email}.`);
      } else if (r.data.provider === "log") {
        toast.warning("Email driver not configured (log mode). Check backend logs.");
      } else {
        toast.error("Email send failed — try again.");
      }
      reset();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="share-scorecard-dialog"
    >
      <form
        onSubmit={send}
        className="w-full max-w-2xl bg-black border border-zinc-800 shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-zinc-900 px-5 py-3">
          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-[#FFB000]" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">REFERRAL</div>
              <div className="font-heading font-black text-base tracking-tight">SHARE WITH ACCOUNTANT</div>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="share-close" className="text-zinc-500 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Recipient name (optional)</span>
              <input
                type="text"
                data-testid="share-recipient-name"
                value={recipient.name}
                onChange={(e) => setRecipient({ ...recipient, name: e.target.value })}
                placeholder="e.g. Sarah, our accountant"
                className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Recipient email *</span>
              <input
                type="email"
                required
                data-testid="share-recipient-email"
                value={recipient.email}
                onChange={(e) => setRecipient({ ...recipient, email: e.target.value })}
                placeholder="sarah@accountingfirm.com.au"
                className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
              />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <Sparkles className="w-3 h-3 text-[#FFB000]" />
                Cover note (DARA-drafted · fully editable)
              </span>
              <button
                type="button"
                onClick={draftNote}
                disabled={drafting}
                data-testid="share-redraft"
                className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 hover:text-[#FFB000] flex items-center gap-1 disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${drafting ? "animate-spin" : ""}`} />
                {drafting ? "DRAFTING…" : "RE-DRAFT"}
              </button>
            </div>
            <textarea
              data-testid="share-cover-note"
              value={coverNote}
              onChange={(e) => setCoverNote(e.target.value)}
              rows={7}
              placeholder={drafting ? "DARA is drafting your note…" : "Hi <accountant>, attached is a hygiene report on our Xero books…"}
              className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white leading-relaxed focus:outline-none focus:border-[#FFB000] resize-none"
            />
          </div>

          <div className="border border-zinc-900 bg-zinc-950/50 px-3 py-2 font-mono text-[10px] text-zinc-500 leading-relaxed">
            ⓘ A 1-page branded PDF of the latest hygiene scan will be attached.
            Replies route to your inbox <span className="text-[#FFB000]">({/* sender shown in app context */}you)</span>,
            and the email's footer includes a friendly link back to DARA OS.
          </div>
        </div>

        <div className="flex justify-between items-center border-t border-zinc-900 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="border border-zinc-800 px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white"
          >
            CANCEL
          </button>
          <button
            type="submit"
            disabled={sending || drafting || !recipient.email || !coverNote}
            data-testid="share-send-btn"
            className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-2 hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
          >
            <Send className="w-3.5 h-3.5" />
            {sending ? "SENDING…" : "SEND TO ACCOUNTANT"}
          </button>
        </div>
      </form>
    </div>
  );
}
